/**
 * I9 Data Mapping
 * Based on https://quip-amazon.com/6QQmAzHGXBe2/CaaS-Real-Time-Status
 */
import React from "react";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "../typography";

const supportLink = (
  <Link target="_blank" href="https://amazon.com/applicationhelp">
    here
  </Link>
);
const i9Link = (
  <Link
    target="_blank"
    href="https://www.uscis.gov/i-9-central/form-i-9-acceptable-documents"
  >
    here
  </Link>
);
export enum I9ButtonState {
  Active = "Active",
  Hidden = "Hidden",
  Disabled = "Disabled",
}

export enum I9Type {
  Normal = "Normal",
  Remote = "Remote",
}

const AshStatusPendingSection1Required = "Pending Section 1 - Action Required";
const AshStatusPendingSection1 = "Pending Section 1";
const AshStatusPendingSection2 =
  "Pending Section 2 - Complete At Your Appointment";
const AshStatusPendingSection2Required = "Pending Section 2 - Action Required";
const AshStatusComplete = "Complete";
const AshPendingSection3 = "Pending Section 3";
const AshStatusEmpty = "";

export const MessageYouMustCompleteSection1 = (
  <Text>
    You must complete both sections of your I-9 to start work with Amazon.
    Completing Section 1 of your I-9 is easy, takes less than 5 minutes, and can
    be done from your phone or computer. Click below to get started.
  </Text>
);
const MessageThanksForCompletingSection1 = (
  <Text>
    Thanks for completing Section 1 of your I-9. Please bring your original,
    unexpired I-9 documents with you to your New Hire Appointment where an
    Amazon employee will complete Section 2. A full list of what you can bring
    is found {i9Link}. If you have any questions, please click {supportLink} to
    contact candidate support.
  </Text>
);
const MessageThanksForCompletingI9 = (
  <Text>
    Thanks for completing your I-9. Please see the rest of your Pre-Hire
    Checklist and emails from Amazon for next steps.
  </Text>
);
const MessageAlreadyCompleted = (
  <Text>
    Since you completed an I-9 with Amazon before, you do not need to complete
    Section 1 again. Please bring your original, unexpired I-9 documents with
    you to your New Hire Appointment to update your I-9. A full list of what you
    can bring is found {i9Link}. If you have any questions, please click{" "}
    {supportLink} to contact candidate support.
  </Text>
);
const MessageThanksForCompletingSection1Remote = (
  <Text>
    Thanks for completing Section 1 of your I-9. Please schedule time to meet
    with your selected Authorized Agent in person and show them your original,
    unexpired I-9 documents. A full list of what you can bring is found {i9Link}
    . If you are unable to meet with your Authorized Agent before your scheduled
    New Hire Appointment, please bring your original, unexpired I-9 documents
    with you. If you have any questions, please click {supportLink} to contact
    candidate support.
  </Text>
);
const MessageI9NoLongerActive = (
  <Text>
    Your I-9 is no longer active. You will be required to complete a new I-9
    form when you apply for a job at Amazon. If you are currently applying for a
    job at Amazon, please contact candidate support by clicking {supportLink}.
  </Text>
);

const CAAS_STATUS_TO_MESSAGE = {
  Initiated: MessageYouMustCompleteSection1,
  WaitingForASHToCreateOrder: MessageYouMustCompleteSection1,
  PendingSection1: MessageYouMustCompleteSection1,
  Section1Complete: MessageThanksForCompletingSection1,
  Section2Complete: MessageThanksForCompletingI9,
  PendingSection3: MessageAlreadyCompleted,
  Section3Complete: MessageThanksForCompletingI9,
  "Remote+Section1Complete": MessageThanksForCompletingSection1Remote,
  Archived: MessageI9NoLongerActive,
};

const CAAS_STATUS_TO_BUTTON_STATE = {
  Initiated: I9ButtonState.Active,
  WaitingForASHToCreateOrder: I9ButtonState.Active,
  PendingSection1: I9ButtonState.Active,
  Section1Complete: I9ButtonState.Disabled,
  Section2Complete: I9ButtonState.Disabled,
  PendingSection3: I9ButtonState.Hidden,
  Section3Complete: I9ButtonState.Disabled,
  Archived: I9ButtonState.Hidden,
};

const CAAS_STATUS_TO_ASH_STATUS: Record<string, string> = {
  Initiated: AshStatusPendingSection1Required,
  WaitingForASHToCreateOrder: AshStatusPendingSection1Required,
  PendingSection1: AshStatusPendingSection1Required,
  Section1Complete: AshStatusPendingSection2,
  Section2Complete: AshStatusComplete,
  PendingSection3: AshPendingSection3,
  Section3Complete: AshStatusComplete,
  "Remote+PendingSection1": AshStatusPendingSection1,
  "Remote+Section1Complete": AshStatusPendingSection2Required,
  "Remote+PendingSection3": AshStatusComplete,
  Archived: AshStatusEmpty,
};

const getAshEntity = (
  i9Type: I9Type,
  caasStatus: string,
  dictionary: any
): string => {
  const specificKey = `${i9Type}+${caasStatus}`;
  const commonKey = caasStatus;
  return dictionary[specificKey] || dictionary[commonKey];
};

export const getAshStatus = (i9Type: I9Type, caasStatus: string): string => {
  return getAshEntity(i9Type, caasStatus, CAAS_STATUS_TO_ASH_STATUS);
};

export const getAshButtonState = (
  i9Type: I9Type,
  caasStatus: string
): I9ButtonState => {
  return getAshEntity(
    i9Type,
    caasStatus,
    CAAS_STATUS_TO_BUTTON_STATE
  ) as I9ButtonState;
};

export const getAshMessage = (i9Type: I9Type, caasStatus: string): string => {
  return getAshEntity(i9Type, caasStatus, CAAS_STATUS_TO_MESSAGE);
};
